import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  PreferredSetting,
  SortByOptions,
  SortDirection,
  User,
  UserTimelines,
} from '@wilson/interfaces';

@Pipe({
  name: 'sortUsersByPreferredSettings',
  standalone: true,
})
export class SortUsersByPreferredSettings implements PipeTransform {
  private readonly translate = inject(TranslateService);

  transform<T extends User | UserTimelines>(
    users: T[] | null,
    userPreferredSettings: PreferredSetting | null,
  ): T[] | null {
    let sortedUsers = users;
    if (users && userPreferredSettings) {
      const { sortBy, sortDirection } = userPreferredSettings;
      if (sortBy === SortByOptions.Name) {
        sortedUsers = this.sortByName(users.slice(), sortDirection);
      } else if (sortBy === SortByOptions.Profession) {
        sortedUsers = this.sortByProfession(users.slice(), sortDirection);
      }
    }
    return sortedUsers;
  }

  private sortByName<T extends User | UserTimelines>(
    users: T[],
    direction: SortDirection,
  ): T[] {
    return users.sort((a, b) => {
      const nameA = `${a.lastName} ${a.firstName}`;
      const nameB = `${b.lastName} ${b.firstName}`;

      return direction === SortDirection.Ascending
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }

  private sortByProfession<T extends User | UserTimelines>(
    users: T[],
    direction: SortDirection,
  ): T[] {
    return users.sort((a, b) => {
      const professionNamesA = a.professionIds
        .map((professionId) => {
          return this.translate.instant(`profession.${professionId}`);
        })
        .join(' ');

      const professionNamesB = b.professionIds
        .map((professionId) => {
          return this.translate.instant(`profession.${professionId}`);
        })
        .join(' ');

      return direction === SortDirection.Ascending
        ? professionNamesA.localeCompare(professionNamesB)
        : professionNamesB.localeCompare(professionNamesA);
    });
  }
}
